import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { cn } from '@/utils'
import { useKeyboardFirstCommandList } from './hooks/use-keyboard-first-command-list'

export type MultiSelectChipComboboxProps<T> = {
  /** Selected item ids (multi-select chips shown above the trigger). */
  value: string[]
  onChange: (ids: string[]) => void
  items: T[]
  getId: (item: T) => string
  getLabel: (item: T) => string
  /** Search filter over the full list (selected items stay visible). */
  matchesSearch: (item: T, query: string) => boolean
  triggerLabel: string
  searchPlaceholder: string
  loadingMessage: string
  errorMessage: string
  /** Shown in place of the list when the typed search matches nothing. Defaults to a generic
   *  message — callers whose items have a more specific name (e.g. "keywords") should pass their
   *  own (e.g. "No matching keywords") since this component is shared across unrelated pickers
   *  (keywords, topics, tags). */
  emptyMessage?: string
  chipsTestId: string
  isLoading?: boolean
  isError?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Keyboard-first opening (Backgrounds keywords): the search field takes focus on open, Tab moves
   * focus onto the option list and Shift+Tab back, and the arrow keys move through the options
   * from either — instead of scrolling the surrounding panel. Enter, or Space on the list, toggles
   * the highlighted option and keeps the list open. Nothing is highlighted until the
   * user moves through the list (arrows, typing, hover), so Enter on an untouched list still just
   * closes it, like a second Enter on the trigger (WMPCM-11322). Off: focus stays on the trigger.
   * See `useKeyboardFirstCommandList`.
   */
  focusSearchOnOpen?: boolean
}

/**
 * Shared multi-select combobox with dismissible chips above the trigger (WMPCM-8596).
 * Selected ids stay in the open list with a checkmark and can be toggled off.
 */
export function MultiSelectChipCombobox<T>({
  value,
  onChange,
  items,
  getId,
  getLabel,
  matchesSearch,
  triggerLabel,
  searchPlaceholder,
  loadingMessage,
  errorMessage,
  emptyMessage = 'No results found.',
  chipsTestId,
  isLoading = false,
  isError = false,
  open,
  onOpenChange,
  focusSearchOnOpen = false,
}: MultiSelectChipComboboxProps<T>) {
  const [search, setSearch] = useState('')

  const selectedIds = useMemo(() => new Set(value), [value])

  const selectedItems = useMemo(
    () =>
      value
        .map((id) => items.find((item) => getId(item) === id))
        .filter((item): item is T => item != null),
    [getId, items, value]
  )

  const visibleItems = useMemo(() => {
    const query = search.trim()

    if (!query) return items

    return items.filter((item) => matchesSearch(item, query))
  }, [items, matchesSearch, search])

  const visibleLabels = useMemo(() => visibleItems.map(getLabel), [getLabel, visibleItems])

  const keyboard = useKeyboardFirstCommandList({
    enabled: focusSearchOnOpen,
    visibleLabels,
    close: () => handleOpenChange(false),
    pick: (label) => {
      const item = visibleItems.find((candidate) => getLabel(candidate) === label)

      if (item) handleToggle(getId(item))
    },
  })

  function handleToggle(id: string) {
    if (selectedIds.has(id)) {
      onChange(value.filter((selectedId) => selectedId !== id))
    } else {
      onChange([...value, id])
    }

    // Keep the menu open for additional multi-selects (WMPCM-8596).
    onOpenChange(true)
  }

  function handleRemove(id: string) {
    onChange(value.filter((selectedId) => selectedId !== id))
  }

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen)
    // ESC / dismiss only clears the search box — chips and selection stay.
    if (!nextOpen) setSearch('')
    if (nextOpen) keyboard.resetHighlight()
  }

  return (
    <div className="w-full min-w-0">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            ref={keyboard.triggerRef}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={triggerLabel}
            className="flex h-9 w-full min-w-0 items-center justify-between rounded-md border border-input bg-background px-3 text-sm font-medium shadow-xs"
          >
            <span className="truncate text-sm font-medium text-foreground">{triggerLabel}</span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] min-w-0 max-w-none p-0"
          align="start"
          // Keeps focus on the trigger button when the popover opens (click or Enter) instead of
          // Radix's default of auto-focusing the first focusable descendant (the search input
          // here). Without this, a second Enter press on the trigger — meant to close the popover
          // the same way a second click does — lands on cmdk's own Enter handling instead and
          // silently picks the first/highlighted item rather than closing (WMPCM-11322).
          //
          // With `focusSearchOnOpen` the search field takes focus instead; Enter there closes the
          // list until an option has been highlighted (see `handleSearchKeyDown`).
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            keyboard.focusSearch()
          }}
        >
          <Command shouldFilter={false} {...keyboard.commandProps}>
            <CommandInput
              ref={keyboard.inputRef}
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={(next) => {
                keyboard.markSearchTyped()
                setSearch(next)
              }}
              onKeyDown={keyboard.searchKeyDown}
            />
            <CommandList ref={keyboard.setListElement} {...keyboard.listProps}>
              {isLoading && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {loadingMessage}
                </div>
              )}
              {isError && !isLoading && (
                <div className="py-6 px-3 text-center text-sm text-destructive">{errorMessage}</div>
              )}
              {!isLoading && !isError && (
                <>
                  <CommandEmpty>{emptyMessage}</CommandEmpty>
                  <CommandGroup>
                    {visibleItems.map((item) => {
                      const id = getId(item)
                      const label = getLabel(item)
                      const isSelected = selectedIds.has(id)

                      return (
                        <CommandItem key={id} value={label} onSelect={() => handleToggle(id)}>
                          <span className="flex-1 text-left">{label}</span>
                          <Check
                            className={cn(
                              'h-4 w-4 shrink-0',
                              isSelected ? 'opacity-100' : 'opacity-0'
                            )}
                            aria-hidden="true"
                          />
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedItems.length > 0 ? (
        <div
          className="flex flex-wrap content-start items-start gap-2 py-2"
          data-testid={chipsTestId}
        >
          {selectedItems.map((item) => {
            const id = getId(item)
            const label = getLabel(item)

            return (
              <span
                key={id}
                className="inline-flex h-5 items-center justify-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs font-semibold leading-4 text-secondary-foreground"
              >
                {label}
                <button
                  type="button"
                  className="inline-flex"
                  aria-label={`Remove ${label}`}
                  onClick={() => handleRemove(id)}
                >
                  <X className="size-3 text-muted-foreground hover:text-foreground" />
                </button>
              </span>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
